import { Box, Button, Typography } from '@mui/material'
import { ListingPage } from 'components/ListingPage/ListingPage'
import LoadingIndicator from 'components/LoadingIndicator/LoadingIndicator'
import { SearchBar } from 'components/SearchBar/SearchBar'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import { Column } from 'components/Table/Table'
import { usePlaces as useGetPlaces } from 'hooks/usePlaces'
import theme from 'lib/theme'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation, useNavigate } from 'react-router-dom'
import Place from 'types/model/place'

const PlacesSelectionHeader = ({
  handleSearch,
  query,
  hasListItems,
}: {
  hasListItems: boolean
  query: string | null
  handleSearch: (query?: string) => void
}) => {
  const navigate = useNavigate()
  const { t } = useTranslation()
  const [input, setInput] = useState<string>('')
  const clearSearch = () => {
    setInput('')
    navigate('/route')
  }

  return (
    <Box mb={2}>
      <Box
        display='flex'
        justifyContent='space-between'
        sx={{
          [theme.breakpoints.between('xs', 'sm')]: {
            flexDirection: 'column',
          },
        }}>
        <Typography variant='h1' mb={3}>
          {t('routes')}
        </Typography>
        <Box
          sx={{
            [theme.breakpoints.up('sm')]: {
              display: 'flex',
            },
          }}>
          <SearchBar
            input={input}
            setInput={setInput}
            placeholderText={t('searchPlacesPlaceholder')}
            handleClick={handleSearch}
            ariaLabel={t('searchPlaces')}
          />
        </Box>
      </Box>
      {!query && (
        <Typography variant='h2' sx={{ fontWeight: 'bold' }}>
          {t('routePlanning.selectStore')}
        </Typography>
      )}
      <Box>
        {query && (
          <Box>
            <Box>
              <Button
                onClick={clearSearch}
                sx={{
                  background: 'transparent',
                  color: theme.palette.primary.main,
                  padding: 0,
                  marginBottom: theme.spacing(1),
                  '&:hover': {
                    background: 'transparent',
                    color: theme.palette.primary.dark,
                  },
                }}>
                <ArrowBackIcon
                  sx={{
                    fill: theme.palette.primary.main,
                    marginRight: theme.spacing(1),
                    '&:hover': { fill: theme.palette.primary.dark },
                  }}
                />
                <Typography>{t('allStores')}</Typography>
              </Button>
              {hasListItems ? (
                <Box display='flex'>
                  <Typography
                    variant='h2'
                    sx={{ marginBottom: theme.spacing(1) }}>
                    {t('searchResults')}
                  </Typography>
                  <Typography variant='h2' sx={{ fontWeight: 'bold' }}>
                    &nbsp;&quot;{query}&quot;
                  </Typography>
                </Box>
              ) : (
                <Box display='flex'>
                  <Typography
                    variant='h2'
                    sx={{ marginBottom: theme.spacing(1) }}>
                    {t('noSearchResultWithQuery')}
                  </Typography>
                  <Typography variant='h2' sx={{ fontWeight: 'bold' }}>
                    &nbsp;&quot;{query}&quot;
                  </Typography>
                </Box>
              )}
            </Box>
            {!hasListItems && (
              <Typography variant='body1'>{t('noSearchResults')}</Typography>
            )}
          </Box>
        )}
      </Box>
    </Box>
  )
}

export const PlacesSelection = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const location = useLocation()
  const parameters = new URLSearchParams(location.search)
  const pageIndex = parameters.get('page')
  const query = parameters.get('search')
  const rowsPerPage = 10
  const { isLoading, places, error, pagination } = useGetPlaces(
    Number(pageIndex) || 1,
    rowsPerPage,
    query
  )

  const columns: Column<
    'name' | 'group' | 'chain.name' | 'city' | 'country'
  >[] = [
    { id: 'name', label: t('name'), minWidth: 170 },
    { id: 'group', label: t('group'), minWidth: 170 },
    {
      id: 'chain.name',
      label: t('chain'),
      minWidth: 170,
    },
    {
      id: 'city',
      label: t('city'),
      minWidth: 170,
    },
    {
      id: 'country',
      label: t('country'),
      minWidth: 170,
    },
  ]

  const handleChangePage = (newPage: number) => {
    if (newPage > 0 && newPage <= pagination.totalPages) {
      if (query) {
        navigate(`/route?page=${newPage}&search=${query}`)
      } else {
        navigate(`/route?page=${newPage}`)
      }
    }
  }

  const handleNavigate = (id: string) => {
    localStorage.setItem('routesListingPage', location.search)
    navigate(`/route/${id}`)
  }

  const getValue = (place: Place, id: string) => {
    const data = id.split('.').reduce((p, prop) => {
      return p[prop as keyof typeof place] as unknown as Place
    }, place)

    return data
  }

  const handleSearch = (query?: string) => {
    navigate(`/route?search=${query}`)
  }

  if (isLoading || !places) return <LoadingIndicator />
  if (error) return <h1>Error</h1>
  return (
    <Box m={4}>
      <ListingPage
        query={query || undefined}
        tableWidth='calc(100vw - 64px - 40px)'
        columns={columns}
        listItems={places}
        handleNavigate={handleNavigate}
        header={
          <PlacesSelectionHeader
            hasListItems={Boolean(places && places.length > 0)}
            handleSearch={handleSearch}
            query={query}
          />
        }
        pagination={pagination}
        page={Number(pageIndex) || 1}
        handleChangePage={handleChangePage}
        displayValues={getValue}
      />
    </Box>
  )
}
